import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { AWAssetDto } from '@api/Api';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { ReactComponent as MenuSecondarySvg } from '@assets/icons/menu-secondary.svg';
import { ReactComponent as ClockSvg } from '@assets/icons/clock.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';

import './AssetModalHead.less';
import classNames from 'classnames';

type AssetModalHeadProps = {
  asset: AWAssetDto;
  approvalWorkflowName: string | null;
  approvalWorkflowId: string;
  campaignName: string;
  isPastDueDateCurrentStage: boolean;
  onClose: () => void;
};

export default function AssetModalHead({
  asset,
  approvalWorkflowName,
  approvalWorkflowId,
  campaignName,
  isPastDueDateCurrentStage,
  onClose
}: AssetModalHeadProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const preparePreview = useMemo(() => {
    return assetPreviewThumbnails({
      url: asset?.previewUrl || '',
      status: 'ready',
      type: asset.type,
      size: 45,
      format: 'img'
    });
  }, [asset]);

  const onOpenAsset = () => {
    onClose();
    const assetUrl = asset.hasAccess
      ? `/media-viewer/${asset.id}/${asset.versionId}`
      : `/approval?id=${approvalWorkflowId}&assetId=${asset.id}`;
    navigate(assetUrl, {
      state: {
        returnTo: location.pathname + location.search
      }
    });
  };

  return (
    <div className="approval-asset-modal_head">
      <div
        className={classNames('approval-asset-modal_head-image', {
          'approval-asset-modal_head-image--disabled': asset.accessDisabled
        })}
      >
        <div className="approval-asset-modal_head-image-lock-icon">
          <LockSvg />
        </div>
        {typeof preparePreview === 'string' ? (
          <img src={preparePreview || ''} alt="" />
        ) : (
          preparePreview
        )}
      </div>
      <div className="approval-asset-modal_head-content">
        <div className="approval-asset-modal_head-info">
          <Tooltip
            title="Open in media viewer"
            overlayClassName="toggle_favorite_overlay"
          >
            <div
              onClick={onOpenAsset}
              className="approval-asset-modal_head-name"
            >
              {asset.name}
              <span>.{asset.extension}</span>
            </div>
          </Tooltip>
          <div className="approval-asset-modal_head-version">
            V{asset.versionsCount}
          </div>
          {isPastDueDateCurrentStage && (
            <div className="approval-asset-modal_head-label">
              <ClockSvg />
              Overdue
            </div>
          )}
        </div>

        <div className="approval-asset-modal_head-breadcrumbs">
          <Tooltip
            title={`${campaignName} project`}
            overlayClassName="toggle_favorite_overlay"
            className="approval-asset-modal_head-breadcrumbs-item"
            placement="top"
          >
            <div className="approval-asset-modal_head-breadcrumbs-icon">
              <MenuSecondarySvg />
            </div>
            <div className="approval-asset-modal_head-breadcrumbs-name">
              {campaignName} project
            </div>
          </Tooltip>
          {approvalWorkflowName && (
            <>
              <div className="approval-asset-modal_head-breadcrumbs-separator">
                <ArrowDownSvg />
              </div>
              <Tooltip
                title={`Routing ${approvalWorkflowName}`}
                overlayClassName="toggle_favorite_overlay"
                className="approval-asset-modal_head-breadcrumbs-item"
                placement="top"
              >
                <div className="approval-asset-modal_head-breadcrumbs-icon">
                  <ApprovalRoutingSvg />
                </div>
                <div className="approval-asset-modal_head-breadcrumbs-name">
                  Routing {approvalWorkflowName}
                </div>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
